/**
 * Tiny API client for the legacy PHP backend.
 * - Auto-attaches JWT from localStorage
 * - In dev (vite proxy), requests start with `/api/...`
 * - In prod, when the SPA is served from `/v8/`, we still hit `../api/...`
 *   on the same origin so cookies + JWT work without CORS.
 */

const TOKEN_KEY = 'auth_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function resolveUrl(path: string): string {
  // Allow callers to pass either "api/foo.php" or "./api/foo.php" or "/api/foo.php"
  let p = path.replace(/^\.?\//, '');
  if (!p.startsWith('api/')) p = `api/${p}`;
  // PHP API always lives at `/api/` on the same origin — works whether the
  // SPA is deployed at `/`, `/v8/`, or any other sub-path. In dev, the vite
  // proxy intercepts `/api/` and forwards to the live PHP server.
  return `/${p}`;
}

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit & { method?: string; json?: unknown } = {}
): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(resolveUrl(path), {
    ...options,
    headers,
    body: options.json !== undefined ? JSON.stringify(options.json) : options.body,
    credentials: 'same-origin',
  });

  let body: unknown;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    body = await res.json();
  } else {
    body = await res.text();
  }

  if (!res.ok) {
    const err = new Error(
      (body as { message?: string })?.message ||
        `HTTP ${res.status} ${res.statusText}`
    ) as ApiError;
    err.status = res.status;
    err.body = body;
    if (res.status === 401) {
      clearToken();
    }
    throw err;
  }

  return body as T;
}
